import { useEffect, useRef, useMemo } from 'react';
import { 
  createChart, 
  IChartApi, 
  CandlestickData, 
  Time, 
  createSeriesMarkers,
  CandlestickSeries,
  ISeriesApi,
  SeriesType
} from 'lightweight-charts';
import { AlphaQuantTrade } from '@/hooks/useAlphaQuantSignals';
import { EnrichedCandleData } from '@/services/binanceService';

interface AlphaQuantChartProps {
  data: EnrichedCandleData[];
  trades: AlphaQuantTrade[];
  height?: number;
}

interface MarkerData {
  time: Time;
  position: 'aboveBar' | 'belowBar' | 'inBar';
  color: string;
  shape: 'circle' | 'square' | 'arrowUp' | 'arrowDown';
  text: string;
  size?: number;
}

export const AlphaQuantChart = ({ data, trades, height = 400 }: AlphaQuantChartProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<ISeriesApi<SeriesType> | null>(null);
  const markersRef = useRef<ReturnType<typeof createSeriesMarkers> | null>(null);

  // Convert data to candlestick format
  const candleData = useMemo((): CandlestickData<Time>[] => {
    return data.map(candle => ({
      time: (candle.timestamp / 1000) as Time,
      open: candle.open,
      high: candle.high,
      low: candle.low,
      close: candle.close,
    }));
  }, [data]);

  // Create trade markers
  const markers = useMemo((): MarkerData[] => {
    return trades.map(trade => {
      const isEntry = trade.type === 'entry';
      const isLong = trade.side === 'long';
      const isWin = (trade.pnl || 0) > 0;
      
      let color: string;
      let shape: 'circle' | 'square' | 'arrowUp' | 'arrowDown';
      let position: 'aboveBar' | 'belowBar' | 'inBar';
      let text: string;
      
      if (isEntry) {
        // Entry markers
        if (isLong) {
          color = '#22c55e'; // green
          shape = 'arrowUp';
          position = 'belowBar';
          text = `▲ ${trade.signalType}`;
        } else {
          color = '#ef4444'; // red
          shape = 'arrowDown';
          position = 'aboveBar';
          text = `▼ ${trade.signalType}`;
        }
      } else {
        // Exit markers
        if (isWin) {
          color = '#22c55e'; // green for profit
        } else {
          color = '#ef4444'; // red for loss
        }
        shape = 'circle';
        position = isLong ? 'aboveBar' : 'belowBar';
        const pnlStr = trade.pnl ? `${trade.pnl > 0 ? '+' : ''}${trade.pnl.toFixed(1)}%` : '';
        text = `✕ ${pnlStr}`;
      }
      
      return {
        time: (trade.timestamp / 1000) as Time,
        position,
        color,
        shape,
        text,
        size: isEntry ? 2 : 1,
      };
    });
  }, [trades]);

  // Initialize chart
  useEffect(() => {
    if (!containerRef.current) return;

    // Create chart
    const chart = createChart(containerRef.current, {
      layout: {
        background: { color: 'transparent' },
        textColor: '#9ca3af',
      },
      grid: {
        vertLines: { color: 'rgba(255, 255, 255, 0.05)' },
        horzLines: { color: 'rgba(255, 255, 255, 0.05)' },
      },
      crosshair: {
        mode: 1,
        vertLine: {
          color: 'rgba(255, 255, 255, 0.2)',
          width: 1,
          style: 2,
        },
        horzLine: {
          color: 'rgba(255, 255, 255, 0.2)',
          width: 1,
          style: 2,
        },
      },
      rightPriceScale: {
        borderColor: 'rgba(255, 255, 255, 0.1)',
      },
      timeScale: {
        borderColor: 'rgba(255, 255, 255, 0.1)',
        timeVisible: true,
        secondsVisible: false,
      },
      handleScale: {
        axisPressedMouseMove: true,
      },
      handleScroll: {
        vertTouchDrag: false,
      },
    });

    chartRef.current = chart;

    // Add candlestick series using v5 API
    const series = chart.addSeries(CandlestickSeries, {
      upColor: '#22c55e',
      downColor: '#ef4444',
      borderUpColor: '#22c55e',
      borderDownColor: '#ef4444',
      wickUpColor: '#22c55e',
      wickDownColor: '#ef4444',
    });

    seriesRef.current = series;

    // Handle resize
    const handleResize = () => {
      if (containerRef.current && chartRef.current) {
        chartRef.current.applyOptions({
          width: containerRef.current.clientWidth,
          height: height,
        });
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
      }
      seriesRef.current = null;
      markersRef.current = null;
    };
  }, [height]);

  // Update data and markers
  useEffect(() => {
    if (!seriesRef.current || candleData.length === 0) return;

    // Set candle data
    seriesRef.current.setData(candleData);
    
    // Remove old markers if they exist
    if (markersRef.current) {
      markersRef.current.setMarkers([]);
    }
    
    // Create new markers using v5 API
    if (markers.length > 0 && seriesRef.current) {
      markersRef.current = createSeriesMarkers(seriesRef.current, markers);
    }

    // Fit content
    if (chartRef.current) {
      chartRef.current.timeScale().fitContent();
    }
  }, [candleData, markers]);

  // Empty state
  if (data.length === 0) {
    return (
      <div 
        className="flex items-center justify-center bg-muted/20 rounded-lg"
        style={{ height }}
      >
        <div className="text-center text-muted-foreground">
          <p className="text-sm">Execute o backtest para visualizar o gráfico</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef} 
      className="w-full rounded-lg overflow-hidden"
      style={{ height }}
    />
  );
};

export default AlphaQuantChart;
